import React, { useMemo } from 'react';
import { View, Text, StyleSheet } from 'react-native';
import { ActivityIndicator } from 'react-native-paper';
import LinearGradient from 'react-native-linear-gradient';
import { useAuth } from '../context/AuthContext';
import DepositService from '../services/depositService';
import { Deposit } from '../types/Deposit';
import { useFirestoreSubscription } from '../hooks/useFirestoreSubscription';
import { formatAmount as fmt } from '../utils/formatAmount';

interface Stats {
  totalAmount: number;
  totalCount: number;
  completedAmount: number;
  completedCount: number;
  pendingAmount: number;
  pendingCount: number;
}

function computeStats(deposits: Deposit[]): Stats {
  const completed = deposits.filter(d => d.status === 'completed');
  const pending = deposits.filter(d => d.status === 'pending');
  return {
    totalAmount: deposits.reduce((sum, d) => sum + d.amount, 0),
    totalCount: deposits.length,
    completedAmount: completed.reduce((sum, d) => sum + d.amount, 0),
    completedCount: completed.length,
    pendingAmount: pending.reduce((sum, d) => sum + d.amount, 0),
    pendingCount: pending.length,
  };
}

const DepositSummaryCard: React.FC = () => {
  const { user } = useAuth();
  const { data: deposits, loading } = useFirestoreSubscription(
    (onUpdate, onError) => {
      if (!user?.uid) { onUpdate([]); return () => {}; }
      return DepositService.subscribeToAllDeposits(onUpdate, onError);
    },
    [] as Deposit[],
  );
  const stats = useMemo(() => computeStats(deposits), [deposits]);
  const s = loading ? null : stats;

  return (
    <View style={styles.wrapper}>
      <LinearGradient
        colors={['#063669', '#274e82']}
        start={{ x: 0, y: 0 }}
        end={{ x: 1, y: 1 }}
        style={styles.card}
      >
        {s === null ? (
          <View style={styles.loadingContainer}>
            <ActivityIndicator testID="activity-indicator" size="small" color="white" />
          </View>
        ) : (
          <>
            <View style={styles.heroRow}>
              <View style={styles.heroLeft}>
                <Text style={styles.heroLabel}>TOTAL DEPOSITS</Text>
                <Text style={styles.heroAmount}>{fmt(s.totalAmount)}</Text>
              </View>
              <View style={styles.badge}>
                <Text style={styles.badgeCount}>{s.totalCount}</Text>
                <Text style={styles.badgeLabel}>deposits</Text>
              </View>
            </View>

            <View style={styles.tilesRow}>
              <View style={styles.tile}>
                <Text style={styles.tileLabel}>Completed</Text>
                <Text style={styles.tileCount}>{s.completedCount} deposits</Text>
                <Text style={styles.tileAmount}>{fmt(s.completedAmount)}</Text>
              </View>
              <View style={[styles.tile, styles.pendingTile]}>
                <Text style={styles.tileLabel}>Pending</Text>
                <Text style={[styles.tileCount, styles.pendingText]}>{s.pendingCount} deposits</Text>
                <Text style={[styles.tileAmount, styles.pendingText]}>{fmt(s.pendingAmount)}</Text>
              </View>
            </View>
          </>
        )}
      </LinearGradient>
    </View>
  );
};

const styles = StyleSheet.create({
  wrapper: {
    paddingHorizontal: 20,
  },
  card: {
    borderRadius: 6,
    marginBottom: 12,
    padding: 20,
    shadowColor: '#111c2d',
    shadowOffset: { width: 0, height: 12 },
    shadowOpacity: 0.06,
    shadowRadius: 40,
    elevation: 4,
  },
  loadingContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 20,
  },
  heroRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 16,
  },
  heroLeft: { flex: 1 },
  heroLabel: {
    fontFamily: 'Inter-Regular',
    fontSize: 11,
    color: 'rgba(255,255,255,0.7)',
    letterSpacing: 1.2,
    textTransform: 'uppercase',
    marginBottom: 6,
  },
  heroAmount: {
    fontFamily: 'Manrope-Bold',
    fontSize: 34,
    color: 'white',
  },
  badge: {
    backgroundColor: 'rgba(255,255,255,0.15)',
    borderRadius: 20,
    paddingHorizontal: 12,
    paddingVertical: 6,
    alignItems: 'center',
  },
  badgeCount: {
    fontFamily: 'Manrope-Bold',
    fontSize: 18,
    color: 'white',
    lineHeight: 22,
  },
  badgeLabel: {
    fontFamily: 'Inter-Regular',
    fontSize: 10,
    color: 'rgba(255,255,255,0.8)',
  },
  tilesRow: {
    flexDirection: 'row',
    gap: 10,
  },
  tile: {
    flex: 1,
    backgroundColor: 'rgba(255,255,255,0.1)',
    borderRadius: 6,
    padding: 14,
  },
  pendingTile: {
    borderWidth: 1,
    borderColor: 'rgba(255,180,100,0.4)',
  },
  tileLabel: {
    fontFamily: 'Inter-Regular',
    fontSize: 10,
    color: 'rgba(255,255,255,0.75)',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    marginBottom: 4,
  },
  tileCount: {
    fontFamily: 'Manrope-SemiBold',
    fontSize: 16,
    color: 'white',
    lineHeight: 20,
  },
  tileAmount: {
    fontFamily: 'Inter-Regular',
    fontSize: 12,
    color: 'rgba(255,255,255,0.8)',
    marginTop: 2,
  },
  pendingText: {
    color: '#ffb59a',
  },
});

export default DepositSummaryCard;
